// Layered visualisation: mirrored frequency spectrum (soft, behind) +
// time-domain oscilloscope (bright, on top). The mirrored bars give a
// "standing wave" look that reads as music; the oscilloscope shows the
// actual waveshape.
//
// It only animates while the analyser actually carries signal. A permanently
// pending animation frame costs battery on a phone and keeps the page "busy"
// forever (which is what blocks screenshot tooling), so when the sound stops
// the rAF is cancelled outright — not merely skipped. Coming back is covered
// twice over: a cheap low-rate probe of the analyser, and wake() on any user
// gesture (every sound starts from one) so the first frame is never late.

/** Silence probe cadence while the loop is parked. */
const PROBE_MS = 200;
/** Keep animating this long after the last non-silent frame (release tails). */
const HOLD_MS = 600;

export class Visualizer {
  private readonly ctx2d: CanvasRenderingContext2D;
  private readonly timeData: Uint8Array<ArrayBuffer>;
  private readonly freqData: Uint8Array<ArrayBuffer>;
  private raf = 0;
  private probeId = 0;
  private armed = false;
  private lastSignal = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly analyser: AnalyserNode,
  ) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2D canvas context unavailable");
    this.ctx2d = ctx;
    this.timeData = new Uint8Array(new ArrayBuffer(analyser.fftSize));
    this.freqData = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));
    this.resize();
    window.addEventListener("resize", () => {
      this.resize();
      this.render(); // resizing clears the canvas, so repaint even while parked
    });
  }

  private resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    this.canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    this.canvas.height = Math.max(1, Math.floor(rect.height * dpr));
    this.ctx2d.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /** Arm the visualiser: from here on it animates whenever there's sound. */
  start(): void {
    if (this.armed) return;
    this.armed = true;
    document.addEventListener("visibilitychange", this.onVisibility);
    document.addEventListener("pointerdown", this.wake, { passive: true });
    document.addEventListener("keydown", this.wake, { passive: true });
    this.wake();
  }

  stop(): void {
    this.armed = false;
    document.removeEventListener("visibilitychange", this.onVisibility);
    document.removeEventListener("pointerdown", this.wake);
    document.removeEventListener("keydown", this.wake);
    this.park();
    this.stopProbe();
  }

  /** Start drawing now — call it right before something is about to sound. */
  wake = (): void => {
    if (!this.armed || document.hidden) return;
    this.lastSignal = performance.now();
    if (this.raf) return;
    this.stopProbe();
    this.raf = requestAnimationFrame(this.frame);
  };

  private frame = (): void => {
    this.raf = 0;
    const now = performance.now();
    if (this.hasSignal()) this.lastSignal = now;
    this.render();
    // Nothing sounding for a while (or tab in the background)? Stop dead.
    if (document.hidden || now - this.lastSignal > HOLD_MS) {
      this.park();
      return;
    }
    this.raf = requestAnimationFrame(this.frame);
  };

  /** Drop the animation frame and fall back to the cheap silence probe. */
  private park(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.startProbe();
  }

  private startProbe(): void {
    if (this.probeId || !this.armed || document.hidden) return;
    this.probeId = window.setInterval(() => {
      if (this.hasSignal()) this.wake();
    }, PROBE_MS);
  }

  private stopProbe(): void {
    if (this.probeId) clearInterval(this.probeId);
    this.probeId = 0;
  }

  private onVisibility = (): void => {
    if (document.hidden) {
      this.park();
      this.stopProbe();
    } else {
      this.wake();
    }
  };

  /** Is anything sounding? Straight off the analyser, every 4th bin. */
  private hasSignal(): boolean {
    this.analyser.getByteFrequencyData(this.freqData);
    for (let i = 0; i < this.freqData.length; i += 4) {
      if (this.freqData[i] > 2) return true;
    }
    return false;
  }

  private render(): void {
    const ctx = this.ctx2d;
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    const mid = h * 0.5;

    ctx.clearRect(0, 0, w, h);

    // === Background: mirrored frequency spectrum (standing-wave silhouette) ===
    this.analyser.getByteFrequencyData(this.freqData);
    // Focus on low/mid bins — they carry most of the visual interest.
    const usedBins = Math.floor(this.freqData.length * 0.55);
    const barW = w / usedBins;
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0.0, "rgba(0, 191, 255, 0.05)");
    grad.addColorStop(0.5, "rgba(0, 191, 255, 0.55)");
    grad.addColorStop(1.0, "rgba(0, 191, 255, 0.05)");
    ctx.fillStyle = grad;
    ctx.globalAlpha = 0.45;
    for (let i = 0; i < usedBins; i++) {
      const v = this.freqData[i] / 255;
      const halfH = v * (h * 0.46);
      ctx.fillRect(i * barW, mid - halfH, barW + 0.5, halfH * 2);
    }
    ctx.globalAlpha = 1;

    // === Foreground: oscilloscope line with soft glow ===
    this.analyser.getByteTimeDomainData(this.timeData);
    ctx.lineWidth = 2.2;
    ctx.shadowColor = "rgba(0, 191, 255, 0.6)";
    ctx.shadowBlur = 8;
    ctx.strokeStyle = "#00bfff";
    ctx.beginPath();
    const n = this.timeData.length;
    for (let i = 0; i < n; i++) {
      const x = (i / (n - 1)) * w;
      const y = mid - (this.timeData[i] / 128 - 1) * (h * 0.42);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.shadowBlur = 0;
  }
}
